"use client";

import Link from "next/link";
import { type MouseEvent, useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button, NutcakeLongLogo } from "@drpcrd/shared/ui";
import { cn } from "@drpcrd/utils";

const SECTION_LINKS = [
  { label: "Creator intelligence", sectionId: "intelligence" },
  { label: "Blueprint", sectionId: "blueprint" },
  { label: "Data partner / MCP", sectionId: "data-partner" },
  { label: "Workflow", sectionId: "workflow" },
  { label: "Plans", sectionId: "plans" },
];

const SIGN_IN_URL = "https://brand.creatoros.xyz/";
const MOBILE_MENU_ID = "site-navigation-menu";
const MOBILE_MENU_LINK_CLASS_NAME =
  "font-poppins text-primary-700 border-b border-neutral-500 pb-1.5 text-base leading-4 font-semibold";

type Props = {
  isArticle?: boolean;
  permanent?: boolean;
};

/**
 * Unified site navigation for Homepage V2.
 * At the top of the page it blends with the purple hero — no logo, white-coloured buttons.
 * Once the user scrolls past the hero it becomes a sticky white bar with the Nutcake logo
 * and the homepage section links fading in from the left.
 * With `permanent` the white bar is shown from the top of the page.
 * Below the large breakpoint the section links and sign in move into a toggled menu.
 * Section links scroll the homepage without leaving the section hash in the URL.
 */
export const Nav = ({ isArticle = false, permanent = false }: Props) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrolled = permanent || hasScrolled || isMenuOpen;

  useEffect(() => {
    const onScroll = () => setHasScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Arriving with a section hash drops the hash from the URL. Deferred a tick because
  // the router rewrites the URL once more while it hydrates, and the scroll is repeated
  // because rewriting the URL interrupts the browser's own scroll to the section.
  useEffect(() => {
    const sectionId = window.location.hash.slice(1);

    if (window.location.pathname !== "/" || !sectionId) {
      return;
    }

    const timeout = setTimeout(() => {
      window.history.replaceState(null, "", "/");
      document.getElementById(sectionId)?.scrollIntoView();
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  // The open menu covers the screen, so the page behind it should not scroll
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const onMenuToggle = () => setIsMenuOpen((isOpen) => !isOpen);
  const onMenuClose = () => setIsMenuOpen(false);

  const onSectionLinkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    onMenuClose();
    const section = document.getElementById(sectionId);
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey;

    if (window.location.pathname !== "/" || !section || isModifiedClick) {
      return;
    }

    event.preventDefault();
    section.scrollIntoView();
  };

  return (
    <header
      aria-label="Site navigation"
      className={cn(
        "fixed top-0 z-50 flex w-full items-center justify-between px-3 pt-3 pb-2 transition-all duration-300 motion-reduce:transition-none sm:pb-3",
        scrolled ? "bg-white px-2 py-1 sm:py-1.5" : "bg-transparent",
      )}
    >
      <div
        className={cn(
          "flex min-w-0 items-center gap-4 transition-opacity duration-300 motion-reduce:transition-none",
          scrolled ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <Link
          href="/"
          aria-label="Nutcake home"
          tabIndex={scrolled ? 0 : -1}
          className="flex shrink-0 items-center"
        >
          <NutcakeLongLogo className="h-3 w-auto shrink-0 sm:h-3.75" />
        </Link>
        <nav
          aria-label="Homepage sections"
          className="hidden items-center lg:flex"
        >
          {SECTION_LINKS.map((link) => (
            <Link
              key={link.sectionId}
              href={`/#${link.sectionId}`}
              tabIndex={scrolled ? 0 : -1}
              onClick={(event) => onSectionLinkClick(event, link.sectionId)}
              className="px-2 py-1 text-sm leading-2.75 font-semibold whitespace-nowrap text-black underline-offset-3 hover:underline"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-2 lg:gap-3">
        <Button
          as={Link}
          href={SIGN_IN_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="text"
          size="md"
          className={cn(
            "hidden font-semibold lg:inline-flex",
            !scrolled && !isArticle && "text-white!",
          )}
        >
          Sign in
        </Button>
        {isArticle ? (
          <>
            <Button
              as={Link}
              href="/contact"
              variant="filled"
              className="lg:hidden"
            >
              Get offer
            </Button>
            <Button
              as={Link}
              href="/contact"
              variant="filled"
              className="hidden lg:inline-flex"
            >
              Start now
            </Button>
          </>
        ) : (
          <Button
            as={Link}
            href="/contact"
            variant="filled"
            className="max-sm:px-1.5 max-sm:text-xs max-sm:leading-[14px]"
          >
            Start now
          </Button>
        )}
        <button
          type="button"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          aria-controls={MOBILE_MENU_ID}
          onClick={onMenuToggle}
          className={cn(
            "flex size-3 cursor-pointer items-center justify-center lg:hidden",
            scrolled || isArticle ? "text-black" : "text-white",
          )}
        >
          {isMenuOpen ? (
            <X className="size-3" aria-hidden />
          ) : (
            <Menu className="size-3" aria-hidden />
          )}
        </button>
      </div>

      {isMenuOpen && (
        <nav
          id={MOBILE_MENU_ID}
          aria-label="Menu"
          className="absolute inset-x-0 top-full flex h-[calc(100dvh-100%)] flex-col gap-1.5 overflow-y-auto bg-white px-3 pt-4 pb-6 lg:hidden"
        >
          {SECTION_LINKS.map((link) => (
            <Link
              key={link.sectionId}
              href={`/#${link.sectionId}`}
              onClick={(event) => onSectionLinkClick(event, link.sectionId)}
              className={MOBILE_MENU_LINK_CLASS_NAME}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/contact"
            onClick={onMenuClose}
            className={MOBILE_MENU_LINK_CLASS_NAME}
          >
            Get in touch
          </Link>
          <Link
            href={SIGN_IN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onMenuClose}
            className={MOBILE_MENU_LINK_CLASS_NAME}
          >
            Sign in
          </Link>
        </nav>
      )}
    </header>
  );
};
